import { z } from 'zod';
import { dateSchema, queryList, requiredDate } from './fields';
import { Priorities, PRIORITY_RANK, type ProjectStatus, ProjectStatuses, Scenarios } from './enums';
import { compareText, formatCompactMoney, formatHours } from './format';
import { idSchema } from './ids';
import { SORT_ORDERS, type SortOrder } from './list';
import { type ProjectIndicators, projectIndicatorsSchema } from './indicators';
import { yearMonthSchema, yearMonthTextSchema } from './period';

// ───────────────────────────── Pārskats (FR-OVR-001…007) ─────────────────────────────

/** Sort keys (FR-OVR-006); each ascending or descending. */
export const OVERVIEW_SORTS = [
  'name',
  'completion',
  'time',
  'expenses',
  'income',
  'priority',
  'profit',
  'margin',
] as const;
export type OverviewSort = (typeof OVERVIEW_SORTS)[number];
/** Sorting by these reads finance data (ADR 0004). */
export const FINANCE_SORTS: readonly OverviewSort[] = ['expenses', 'income', 'profit', 'margin'];
/** Every status except Cancelled (FR-OVR-002). */
export const OVERVIEW_DEFAULT_STATUSES: readonly ProjectStatus[] = ProjectStatuses.codes.filter(
  (s) => s !== 'cancelled',
);

const periodDate = requiredDate('Lūdzu norādiet abus datumus.');
const periodInOrder: [(q: { from: string; to: string }) => boolean, { path: string[]; message: string }] = [
  (q) => q.from <= q.to,
  { path: ['from'], message: 'Sākuma datumam jābūt pirms beigu datuma.' },
];

/** The period of the overview (FR-OVR-003) — the page checks it before searching. */
export const overviewPeriodSchema = z.object({ from: periodDate, to: periodDate }).refine(...periodInOrder);

export const overviewQuerySchema = z
  .object({
    from: periodDate,
    to: periodDate,
    /** Empty = every status. */
    statuses: queryList(ProjectStatuses.schema),
    sort: z.enum(OVERVIEW_SORTS).default('name'),
    order: z.enum(SORT_ORDERS).default('asc'),
  })
  .refine(...periodInOrder);

export const overviewRowSchema = z.object({
  id: idSchema,
  name: z.string(),
  customerName: z.string(),
  status: ProjectStatuses.schema,
  priority: Priorities.schema,
  startDate: dateSchema,
  plannedFinish: dateSchema,
  completion: z.number().nullable(),
  financialManagement: z.boolean(),
  indicators: projectIndicatorsSchema,
});
export type OverviewRow = z.infer<typeof overviewRowSchema>;

export const overviewSchema = z.object({ rows: z.array(overviewRowSchema) });

/** The number a project is sorted by; no data counts as 0 (FR-OVR-006, parity). */
function sortValue(row: OverviewRow, key: Exclude<OverviewSort, 'name'>): number {
  const i: ProjectIndicators = row.indicators;
  switch (key) {
    case 'completion':
      return row.completion ?? 0;
    case 'time':
      return i.time?.pct ?? 0;
    case 'expenses':
      return i.expenses?.pct ?? 0;
    case 'income':
      return i.income?.pct ?? 0;
    case 'priority':
      return PRIORITY_RANK[row.priority];
    case 'profit':
      return Number(i.profit?.amount ?? 0);
    case 'margin':
      return i.margin?.pct ?? 0;
  }
}

export function compareOverview(
  sort: OverviewSort,
  order: SortOrder,
  a: OverviewRow,
  b: OverviewRow,
): number {
  const primary = sort === 'name' ? compareText(a.name, b.name) : sortValue(a, sort) - sortValue(b, sort);
  return (order === 'asc' ? primary : -primary) || compareText(a.name, b.name);
}

// ───────────────────────────── Roadmap (FR-OVR-013…018) ─────────────────────────────

export const ROADMAP_MAX_MONTHS = 61;
/** Current month + 5 (FR-OVR-013). */
export const ROADMAP_DEFAULT_MONTHS = 6;
export const ROADMAP_DEFAULT_STATUSES: readonly ProjectStatus[] = ['planning', 'active', 'active_hot'];
/** Timesheet amount shown: "Planned revenue" or "Invoiced" (FR-OVR-014). */
export const ROADMAP_FIELDS = ['projectRevenue', 'invoiced'] as const;
export type RoadmapField = (typeof ROADMAP_FIELDS)[number];
export const ROADMAP_UNITS = ['eur', 'hours'] as const;
export type RoadmapUnit = (typeof ROADMAP_UNITS)[number];
export const ROADMAP_DEFAULT_RATE = 75;

export const roadmapQuerySchema = z.object({
  /** `YYYY-MM`, inclusive. */
  from: yearMonthTextSchema,
  to: yearMonthTextSchema,
  statuses: queryList(ProjectStatuses.schema),
  view: Scenarios.schema.default('plan'),
  field: z.enum(ROADMAP_FIELDS).default('projectRevenue'),
});

export const roadmapSchema = z.object({
  /** At most 61 months; empty when `from` is after `to` (FR-OVR-018). */
  months: z.array(yearMonthSchema.extend({ total: z.string() })),
  projects: z.array(
    z.object({
      id: idSchema,
      name: z.string(),
      /** Per month: the amount if above 0, else null. */
      values: z.array(z.string().nullable()),
      /** Plan view of a project without financial management: no amounts, grey blocks where the Plan sheet has months (FR-OVR-016). */
      greyMonths: z.array(z.boolean()).nullable(),
    }),
  ),
});
export type Roadmap = z.infer<typeof roadmapSchema>;

/** A Roadmap cell or month total: compact EUR or whole hours at the given rate (FR-OVR-015). */
export function formatRoadmapValue(amount: string, unit: RoadmapUnit, rate: number): string {
  return unit === 'hours' ? formatHours(Math.round(Number(amount) / rate)) : formatCompactMoney(amount);
}
